import type { MenuScreen, MenuContext } from '../types.js';
import { createScreenShell, createMenuHeader, createCrestButton, createPasteButton, el } from '@bn-games/ui-shell';
import { t } from '../../i18n/I18n.js';
import { parseVariant } from '../../network/protocol.js';
import { ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH, sanitizeRoomCodeInput } from '@bn-games/network-protocol';

export function createOnlineJoinScreen(): MenuScreen {
  let frame: HTMLElement | null = null;
  let codeBuffer = '';
  let slots: HTMLElement[] = [];
  let statusNode: HTMLElement | null = null;
  let variantNode: HTMLElement | null = null;
  let verifiableNode: HTMLElement | null = null;
  let joinSlot: HTMLElement | null = null;
  let unsubAll: (() => void) | null = null;
  let connecting = false;

  function paintSlots(): void {
    for (let i = 0; i < ROOM_CODE_LENGTH; i += 1) {
      const slot = slots[i];
      if (!slot) continue;
      const ch = codeBuffer[i] ?? '';
      const isActive = !connecting && i === codeBuffer.length;
      slot.classList.toggle('code-slot--active', isActive);

      while (slot.firstChild) slot.removeChild(slot.firstChild);
      if (ch) {
        slot.appendChild(document.createTextNode(ch));
      } else if (isActive) {
        const caret = document.createElement('div');
        caret.className = 'code-slot__caret';
        slot.appendChild(caret);
      }
    }
  }

  function setJoinFormVisible(visible: boolean): void {
    for (const slot of slots) {
      slot.style.display = visible ? '' : 'none';
    }
    if (joinSlot) joinSlot.style.display = visible ? '' : 'none';
  }

  function paintStatus(ctx: MenuContext): void {
    if (!statusNode) return;
    const info = ctx.roomClient.getInfo();
    statusNode.classList.remove('waiting-dots');

    if (info.state === 'joining' && info.localSeat !== null) {
      // Seated via resumeRoom's ROOM_JOINED path (a link-seated new player,
      // not a reconnect) — a genuine multi-minute wait in a 3/4-seat room,
      // not the sub-frame "connecting" transient the switch below handles.
      // There's nothing left to type, so hide the code entry UI instead of
      // falling through to the type-a-code hint.
      connecting = true;
      statusNode.textContent = t('online.status.waitingForOpponent');
      statusNode.classList.add('waiting-dots');
      setJoinFormVisible(false);
      paintVariant(ctx);
      paintVerifiable(ctx);
      return;
    }
    setJoinFormVisible(true);

    switch (info.state) {
      case 'connecting':
      case 'joining':
        statusNode.textContent = t('online.status.joining');
        statusNode.classList.add('waiting-dots');
        connecting = true;
        break;
      case 'error':
        statusNode.textContent = info.errorMessage ?? t('online.status.couldNotJoin');
        connecting = false;
        break;
      default:
        statusNode.textContent = codeBuffer.length < ROOM_CODE_LENGTH
          ? t('online.join.typeHint')
          : t('online.join.enterHint');
        connecting = false;
    }
    refreshJoinButton(ctx);
    paintSlots();
    paintVariant(ctx);
    paintVerifiable(ctx);
  }

  function paintVariant(ctx: MenuContext): void {
    if (!variantNode) return;
    const info = ctx.roomClient.getInfo();
    if (info.variant !== null) {
      const variant = parseVariant(info.variant);
      variantNode.textContent = t('online.join.variantLabel', {
        variant: t(`settings.variants.${variant}`),
      });
      variantNode.style.visibility = 'visible';
    } else {
      variantNode.style.visibility = 'hidden';
      variantNode.textContent = '';
    }
  }

  function paintVerifiable(ctx: MenuContext): void {
    if (!verifiableNode) return;
    const info = ctx.roomClient.getInfo();
    // Joiner-side verifiable banner. Visible from the moment ROOM_JOINED
    // lands (RoomClient mirrors the host's preference) through the brief
    // window before GAME_START flips the scene. Implicit consent: staying
    // in the room is agreement, leaving the screen cancels.
    if (info.verifiable) {
      verifiableNode.textContent = t('online.verifiable.joinerOn');
      verifiableNode.style.display = '';
    } else {
      verifiableNode.textContent = '';
      verifiableNode.style.display = 'none';
    }
  }

  function refreshJoinButton(ctx: MenuContext): void {
    if (!joinSlot) return;
    while (joinSlot.firstChild) joinSlot.removeChild(joinSlot.firstChild);
    if (codeBuffer.length === ROOM_CODE_LENGTH && !connecting) {
      const btn = createCrestButton({
        label: t('online.join.submit'),
        primary: true,
        small: true,
        onClick: () => submit(ctx),
      });
      btn.style.width = '160px';
      joinSlot.appendChild(btn);
    }
  }

  function submit(ctx: MenuContext): void {
    if (codeBuffer.length !== ROOM_CODE_LENGTH || connecting) return;
    connecting = true;
    ctx.setOpponent('online');
    ctx.joinRoom(codeBuffer);
    paintStatus(ctx);
  }

  function onKey(ctx: MenuContext, ev: KeyboardEvent): void {
    if (connecting) return;
    if (ev.key === 'Enter') {
      ev.preventDefault();
      submit(ctx);
      return;
    }
    if (ev.key === 'Escape') {
      ev.preventDefault();
      ctx.cancelRoom();
      ctx.navigate('play');
      return;
    }
    if (ev.key === 'Backspace') {
      ev.preventDefault();
      if (codeBuffer.length > 0) {
        codeBuffer = codeBuffer.slice(0, -1);
        paintStatus(ctx);
      }
      return;
    }
    const ch = ev.key.toUpperCase();
    if (ch.length === 1 && ROOM_CODE_ALPHABET.includes(ch) && codeBuffer.length < ROOM_CODE_LENGTH) {
      codeBuffer += ch;
      paintStatus(ctx);
    }
  }

  return {
    mount(root, ctx) {
      const shell = createScreenShell();
      frame = shell.frame;
      const body = shell.body;

      body.appendChild(createMenuHeader({ title: t('online.join.title') }));

      const center = el('div', { class: 'online-center' });

      const label = el('div', { class: 'online-label', text: t('online.join.codeLabel') });
      const slotRow = el('div', { class: 'code-slots' });
      slots = [];
      for (let i = 0; i < ROOM_CODE_LENGTH; i += 1) {
        const slot = el('div', { class: 'code-slot' });
        slots.push(slot);
        slotRow.appendChild(slot);
      }

      statusNode = el('div', { class: 'online-hint', text: t('online.join.typeHint') });
      variantNode = el('div', { class: 'online-hint' });
      variantNode.style.visibility = 'hidden';
      verifiableNode = el('div', { class: 'online-hint' });
      verifiableNode.style.display = 'none';
      joinSlot = el('div', { class: 'online-join-slot' });

      const pasteBtn = createPasteButton({
        label: t('online.join.paste'),
        sanitize: sanitizeRoomCodeInput,
        onPaste: (text) => {
          codeBuffer = text;
          paintStatus(ctx);
        },
      });
      const pasteRow = el('div', { class: 'button-row' }, [pasteBtn]);

      center.appendChild(label);
      center.appendChild(slotRow);
      center.appendChild(pasteRow);
      center.appendChild(statusNode);
      center.appendChild(variantNode);
      center.appendChild(verifiableNode);
      center.appendChild(joinSlot);

      const back = el('div', { class: 'button-row back-button-row' }, [
        createCrestButton({
          label: t('common.back'),
          small: true,
          onClick: () => {
            ctx.cancelRoom();
            ctx.navigate('play');
          },
        }),
      ]);

      body.appendChild(center);
      body.appendChild(back);
      root.appendChild(frame);

      const handler = (ev: KeyboardEvent): void => onKey(ctx, ev);
      window.addEventListener('keydown', handler);
      const pasteHandler = (ev: ClipboardEvent): void => {
        if (connecting) return;
        const raw = ev.clipboardData?.getData('text') ?? '';
        const sanitized = sanitizeRoomCodeInput(raw);
        if (sanitized.length === 0) return;
        ev.preventDefault();
        codeBuffer = sanitized;
        paintStatus(ctx);
      };
      window.addEventListener('paste', pasteHandler);
      const unsub = ctx.roomClient.onUpdate(() => paintStatus(ctx));
      unsubAll = (): void => {
        window.removeEventListener('keydown', handler);
        window.removeEventListener('paste', pasteHandler);
        unsub();
      };

      const prefill = ctx.consumePendingJoinCode();
      if (prefill) {
        codeBuffer = sanitizeRoomCodeInput(prefill);
      }
      paintStatus(ctx);
    },
    update(ctx) {
      paintStatus(ctx);
    },
    unmount() {
      unsubAll?.();
      unsubAll = null;
      frame?.remove();
      frame = null;
      slots = [];
      statusNode = null;
      variantNode = null;
      verifiableNode = null;
      joinSlot = null;
      codeBuffer = '';
      connecting = false;
    },
  };
}
